import React, { useState, useEffect } from 'react';

// Comps
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { Button } from "@src/stories/elements/Button/Button";
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';

// Hooks
import { useGetCustomerQuery } from '@src/hooks/useGetCustomerQuery';
import useCustomerMarketingUpdate from '@src/hooks/useCustomerMarketingUpdate';

interface AccountCommsProps {
  settings: {
    opt_in_label?: string;
    opt_out_label?: string;
    button_text?: string;
    success_message?: string;
    heading?: string;
    description?: string;
    button_style?: string;
  };
}

const AccountComms: React.FC<AccountCommsProps> = ({ settings }) => {
  const {
    opt_in_label = "Yes, I'd like to receive marketing emails with product updates, special offers, and news.",
    opt_out_label = "No, I don't want to receive marketing emails.",
    button_text = 'Save preferences',
    success_message = 'Your communication preferences have been updated successfully.',
    heading = 'Communication Preferences',
    description = 'Manage your email preferences and stay updated with our latest news and offers.',
    button_style = 'primary'
  } = settings || {};

  // Hooks
  const { fetchCustomer, customer } = useGetCustomerQuery();
  const {
    updateMarketingPreferences,
    loading: updateLoading,
    success: updateSuccess,
    error: updateError
  } = useCustomerMarketingUpdate();

  // State
  const [acceptsMarketing, setAcceptsMarketing] = useState<boolean | null>(null);
  const [originalAcceptsMarketing, setOriginalAcceptsMarketing] = useState<boolean | null>(null);
  const [isUpdated, setIsUpdated] = useState(false);

  // Handle radio change
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const newValue = e.target.id === 'accepts_marketing_yes';
  setAcceptsMarketing(newValue);
  setIsUpdated(originalAcceptsMarketing !== newValue);
  };

  // Handle form submit
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (acceptsMarketing !== null) {
      await updateMarketingPreferences(acceptsMarketing);
      setOriginalAcceptsMarketing(acceptsMarketing);
      setIsUpdated(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setAcceptsMarketing(originalAcceptsMarketing);
    setIsUpdated(false);
  };

  // Fetch customer data on component mount
  useEffect(() => {
    if ((window as any).customer?.id) {
      fetchCustomer((window as any).customer.id, []);
    }
  }, []);

  // Update local state when customer data is loaded
  useEffect(() => {
    if (customer?.acceptsMarketing !== undefined) {
      setAcceptsMarketing(customer.acceptsMarketing);
      setOriginalAcceptsMarketing(customer.acceptsMarketing);
      setIsUpdated(false);
    }
  }, [customer]);

  // Reset success state after 3 seconds
  useEffect(() => {
    if (updateSuccess) {
      const timer = setTimeout(() => {
        // You can add additional logic here if needed
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateSuccess]);

  const content = (
    <div className='flex flex-col w-full'>
      <form onSubmit={handleSubmit} className='space-y-6'>
        {/* Radio Options */}
        <div className='space-y-4'>
          <div className='flex items-start gap-3'>
            <input
              type='radio'
              id='accepts_marketing_yes'
              name='accepts_marketing'
              value='true'
              checked={acceptsMarketing === true}
              onChange={handleRadioChange}
              className='mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 accent-blue-600'
              disabled={updateLoading}
            />
            <label 
              htmlFor='accepts_marketing_yes'
              className='text-sm leading-relaxed cursor-pointer'
            >
              {opt_in_label}
            </label>
          </div>

          <div className='flex items-start gap-3'>
            <input
              type='radio'
              id='accepts_marketing_no'
              name='accepts_marketing'
              value='false'
              checked={acceptsMarketing === false}
              onChange={handleRadioChange}
              className='mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 accent-blue-600'
              disabled={updateLoading}
            />
            <label 
              htmlFor='accepts_marketing_no'
              className='text-sm leading-relaxed cursor-pointer'
            >
              {opt_out_label}
            </label>
          </div>
        </div>

        {/* Success Message */}
        {updateSuccess && (
          <Paragraph
            type='text-center success'
            text={success_message}
            classes='text-green-600 bg-green-50 p-3 rounded-md'
          />
        )}

        {/* Error Message */}
        {updateError && (
          <Paragraph
            type='errors text-center'
            text={updateError}
            classes='text-red-600 bg-red-50 p-3 rounded-md'
          />
        )}

        {/* Save/Cancel Buttons */}
        {(isUpdated && acceptsMarketing !== null) ? (
          <div className='flex gap-3'>
            <Button
              type='button'
              className={`w-1/2 border h-[calc(3rem-2px)] text-base`}
              disabled={updateLoading}
              aria-label='Cancel communication preference changes'
              label='Cancel'
              onClick={handleCancel}
            />
            <Button
              type='submit'
              className={`w-1/2 ${button_style} h-[calc(3rem-2px)] text-base`}
              disabled={updateLoading}
              aria-label='Save communication preferences'
              label={updateLoading ? 'Saving...' : button_text}
            />
          </div>
        ) : null}
      </form>
    </div>
  );

  return (
    <AccountSectionLayout classes='hide-scrollbar' children={content} />
  );
};

export default AccountComms;